import { RATE_TABLE_NAMES, isoDateTime, rateFromHourly, resourceRatePeriod, type ISODateTime, type RatePerMinute, type Resource, type ResourceRatePeriod, type ResourceRateTables, type RateTableName } from '@bigint-project/core';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

export interface ResourceRateTablesDialogProps {
  readonly resource: Resource;
  readonly open: boolean;
  readonly onCancel: () => void;
  readonly onSave: (rateTables: ResourceRateTables) => void;
}

interface DraftPeriod {
  readonly id: number;
  effectiveDate: string;
  standardRateHourly: string;
  overtimeRateHourly: string;
  costPerUse: string;
}

interface PendingDelete {
  readonly table: RateTableName;
  readonly row: DraftPeriod;
}

const TABLE_NAMES = RATE_TABLE_NAMES;

const EMPTY_TABLES = (): Record<RateTableName, DraftPeriod[]> => ({ A: [], B: [], C: [], D: [], E: [] });

const isLocalDateTime = (value: string): boolean => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (parts === null) return false;
  const [, yearText, monthText, dayText, hourText, minuteText] = parts;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const parsed = new Date(Date.UTC(year, month - 1, day, hour, minute));
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day
    && parsed.getUTCHours() === hour
    && parsed.getUTCMinutes() === minute;
};

const toLocalInput = (value: ISODateTime): string => (value as string).slice(0, 16);

const hourly = (rate: RatePerMinute): string => String((rate as number) * 60);

const parseNonnegative = (value: string): number | null => {
  if (value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

function draftFromPeriods(periods: readonly ResourceRatePeriod[]): DraftPeriod[] {
  return periods.map((period, index) => ({
    id: index + 1,
    effectiveDate: toLocalInput(period.effectiveFrom),
    standardRateHourly: hourly(period.standardRate),
    overtimeRateHourly: hourly(period.overtimeRate),
    costPerUse: String(period.costPerUse as number),
  }));
}

function makeDraftTables(rateTables: ResourceRateTables | undefined): Record<RateTableName, DraftPeriod[]> {
  const tables = EMPTY_TABLES();
  for (const table of TABLE_NAMES) tables[table] = draftFromPeriods(rateTables?.[table] ?? []);
  return tables;
}

function createEmptyPeriod(id: number): DraftPeriod {
  return { id, effectiveDate: '', standardRateHourly: '', overtimeRateHourly: '', costPerUse: '' };
}

/**
 * 純 UI rate-table 編輯器。暫時以 Resource 的舊費率作為 Table A 的唯讀 fallback；
 * 實際 rate-table 計價與 store 寫入由宿主與 core 負責。
 */
export function ResourceRateTablesDialog({ resource, open, onCancel, onSave }: ResourceRateTablesDialogProps): React.JSX.Element | null {
  const [activeTable, setActiveTable] = useState<RateTableName>('A');
  const [draftTables, setDraftTables] = useState<Record<RateTableName, DraftPeriod[]>>(() => makeDraftTables(undefined));
  const [error, setError] = useState<string | null>(null);
  const [deletePending, setDeletePending] = useState<PendingDelete | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Partial<Record<RateTableName, HTMLButtonElement>>>({});
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const nextIdRef = useRef(1);
  const titleId = useId();
  const descriptionId = useId();

  const rateTables = resource.rateTables;
  const hasExplicitTableA = (rateTables?.A?.length ?? 0) > 0;
  const activeRows = draftTables[activeTable];
  const hasPopulatedRows = activeRows.some((row) => Object.values(row).some((value) => typeof value === 'string' && value !== ''));

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const nextTables = makeDraftTables(rateTables);
      setDraftTables(nextTables);
      nextIdRef.current = Math.max(1, ...Object.values(nextTables).flat().map((period) => period.id + 1));
      setActiveTable('A');
      setError(null);
      setDeletePending(null);
      returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      tabRefs.current.A?.focus();
    }
    wasOpenRef.current = open;
  }, [open, rateTables]);

  useEffect(() => {
    if (!open) return undefined;
    return () => {
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (deletePending !== null) cancelDeleteRef.current?.focus();
  }, [deletePending]);

  const fallback = useMemo(() => ({
    standardRateHourly: hourly(resource.standardRate),
    overtimeRateHourly: hourly(resource.overtimeRate),
    costPerUse: String(resource.costPerUse as number),
  }), [resource.costPerUse, resource.overtimeRate, resource.standardRate]);

  if (!open) return null;

  const updateRow = (id: number, key: keyof Omit<DraftPeriod, 'id'>, value: string): void => {
    setDraftTables((current) => ({
      ...current,
      [activeTable]: current[activeTable].map((row) => row.id === id ? { ...row, [key]: value } : row),
    }));
    setError(null);
  };

  const addRow = (): void => {
    setDraftTables((current) => ({
      ...current,
      [activeTable]: [...current[activeTable], createEmptyPeriod(nextIdRef.current++)],
    }));
    setError(null);
  };

  const requestRemove = (row: DraftPeriod): void => {
    const populated = [row.effectiveDate, row.standardRateHourly, row.overtimeRateHourly, row.costPerUse].some((value) => value !== '');
    if (!populated) {
      setDraftTables((current) => ({ ...current, [activeTable]: current[activeTable].filter((item) => item.id !== row.id) }));
      return;
    }
    setDeletePending({ table: activeTable, row });
  };

  const confirmRemove = (): void => {
    if (deletePending === null) return;
    setDraftTables((current) => ({
      ...current,
      [deletePending.table]: current[deletePending.table].filter((item) => item.id !== deletePending.row.id),
    }));
    setDeletePending(null);
  };

  const save = (): void => {
    const output: Partial<Record<RateTableName, readonly ResourceRatePeriod[]>> = {};
    for (const table of TABLE_NAMES) {
      let previousDate: string | null = null;
      const periods: ResourceRatePeriod[] = [];
      for (const row of draftTables[table]) {
        if (!isLocalDateTime(row.effectiveDate)) {
          setActiveTable(table);
          setError(`${table} 表的生效日期必須是有效的本機日期時間。`);
          return;
        }
        const standardRateHourly = parseNonnegative(row.standardRateHourly);
        const overtimeRateHourly = parseNonnegative(row.overtimeRateHourly);
        const costPerUse = parseNonnegative(row.costPerUse);
        if (standardRateHourly === null || overtimeRateHourly === null || costPerUse === null) {
          setActiveTable(table);
          setError(`${table} 表的費率與每次使用成本必須是有限的非負數。`);
          return;
        }
        if (previousDate !== null && row.effectiveDate <= previousDate) {
          setActiveTable(table);
          setError(`${table} 表的生效日期必須由早到晚且不可重複。`);
          return;
        }
        previousDate = row.effectiveDate;
        periods.push(resourceRatePeriod(
          isoDateTime(`${row.effectiveDate}:00`),
          rateFromHourly(standardRateHourly) as number,
          rateFromHourly(overtimeRateHourly) as number,
          costPerUse,
        ));
      }
      if (periods.length > 0) output[table] = periods;
    }
    onSave(output);
  };

  const trapFocus = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (deletePending !== null) setDeletePending(null);
      else onCancel();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = (deletePending !== null ? confirmationRef.current : dialogRef.current)?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const selectTab = (table: RateTableName): void => {
    if (deletePending !== null) return;
    setActiveTable(table);
    setError(null);
  };

  const onTabKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, current: RateTableName): void => {
    const currentIndex = TABLE_NAMES.indexOf(current);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % TABLE_NAMES.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + TABLE_NAMES.length) % TABLE_NAMES.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = TABLE_NAMES.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTable = TABLE_NAMES[nextIndex];
    if (nextTable === undefined || deletePending !== null) return;
    selectTab(nextTable);
    tabRefs.current[nextTable]?.focus();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} onKeyDown={trapFocus} className="max-h-full w-full max-w-4xl overflow-auto rounded-lg bg-white p-5 shadow-xl dark:bg-slate-900">
        <div className="flex items-start justify-between gap-4">
          <div><h2 id={titleId} className="text-lg font-semibold">資源費率表</h2><p id={descriptionId} className="mt-1 text-sm text-slate-600 dark:text-slate-300">{resource.name} 的費率表以本機日期時間生效；輸入金額均為每小時。</p></div>
          <button type="button" onClick={onCancel} aria-label="關閉資源費率表" className="rounded px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800">×</button>
        </div>

        <div role="tablist" aria-label="費率表" className="mt-4 flex gap-1 border-b border-slate-200 dark:border-slate-700">
          {TABLE_NAMES.map((table) => <button key={table} ref={(element) => { tabRefs.current[table] = element ?? undefined; }} id={`resource-rate-table-tab-${table}`} type="button" role="tab" aria-selected={activeTable === table} aria-controls={`resource-rate-table-panel-${table}`} tabIndex={activeTable === table ? 0 : -1} disabled={deletePending !== null} onKeyDown={(event) => onTabKeyDown(event, table)} onClick={() => selectTab(table)} className={`rounded-t px-3 py-2 text-sm font-medium ${activeTable === table ? 'bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-100' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}>表 {table}</button>)}
        </div>

        <div id={`resource-rate-table-panel-${activeTable}`} role="tabpanel" aria-labelledby={`resource-rate-table-tab-${activeTable}`}>
        {activeTable === 'A' && !hasExplicitTableA ? <section aria-label="表 A 既有預設費率" className="mt-4 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100"><h3 className="font-medium">既有預設費率（未建立表 A 時的 fallback）</h3><p className="mt-1">標準：{fallback.standardRateHourly}/小時；加班：{fallback.overtimeRateHourly}/小時；每次使用：{fallback.costPerUse}。</p></section> : null}
        {activeTable !== 'A' && activeRows.length === 0 ? <p className="mt-4 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">表 {activeTable} 尚未建立費率期間；指派選用此表時會安全沿用表 A。</p> : null}

        <div className="mt-4 overflow-x-auto"><table className="w-full min-w-[720px] text-left text-sm"><thead><tr className="border-b border-slate-200 dark:border-slate-700"><th className="px-2 py-2 font-medium">生效日期時間</th><th className="px-2 py-2 font-medium">標準費率（每小時）</th><th className="px-2 py-2 font-medium">加班費率（每小時）</th><th className="px-2 py-2 font-medium">每次使用成本</th><th className="px-2 py-2"><span className="sr-only">操作</span></th></tr></thead><tbody>{activeRows.length === 0 ? <tr><td colSpan={5} className="px-2 py-4 text-slate-500">尚無明確費率期間。</td></tr> : activeRows.map((row, index) => <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800"><td className="px-2 py-2"><input aria-label={`表 ${activeTable} 第 ${index + 1} 筆生效日期時間`} type="datetime-local" value={row.effectiveDate} onChange={(event) => updateRow(row.id, 'effectiveDate', event.target.value)} className="w-full rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" /></td><td className="px-2 py-2"><input aria-label={`表 ${activeTable} 第 ${index + 1} 筆標準費率`} type="number" min="0" step="any" value={row.standardRateHourly} onChange={(event) => updateRow(row.id, 'standardRateHourly', event.target.value)} className="w-full rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" /></td><td className="px-2 py-2"><input aria-label={`表 ${activeTable} 第 ${index + 1} 筆加班費率`} type="number" min="0" step="any" value={row.overtimeRateHourly} onChange={(event) => updateRow(row.id, 'overtimeRateHourly', event.target.value)} className="w-full rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" /></td><td className="px-2 py-2"><input aria-label={`表 ${activeTable} 第 ${index + 1} 筆每次使用成本`} type="number" min="0" step="any" value={row.costPerUse} onChange={(event) => updateRow(row.id, 'costPerUse', event.target.value)} className="w-full rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" /></td><td className="px-2 py-2 text-right"><button type="button" onClick={() => requestRemove(row)} className="rounded border border-red-300 px-2 py-1 text-red-700 dark:border-red-800 dark:text-red-300">刪除</button></td></tr>)}</tbody></table></div>
        {error ? <p role="alert" className="mt-3 text-sm text-red-600">{error}</p> : null}
        <div className="mt-4 flex flex-wrap justify-between gap-2"><button type="button" onClick={addRow} className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium dark:border-slate-600">新增費率期間</button><div className="flex gap-2"><button type="button" onClick={onCancel} className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium dark:border-slate-600">取消</button><button type="button" onClick={save} className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500">儲存費率表</button></div></div>

        {deletePending ? <div ref={confirmationRef} role="alertdialog" aria-modal="true" aria-label="確認刪除費率期間" className="mt-4 rounded border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30"><p className="text-sm">{deletePending.table === activeTable && hasPopulatedRows && activeRows.length === 1 ? '這是此費率表最後一筆期間。' : '確定刪除此費率期間？'} 刪除後將在儲存時套用。</p><div className="mt-3 flex justify-end gap-2"><button ref={cancelDeleteRef} type="button" onClick={() => setDeletePending(null)} className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600">取消</button><button type="button" onClick={confirmRemove} className="rounded bg-red-600 px-2 py-1 text-sm text-white">確認刪除</button></div></div> : null}
        </div>
      </div>
    </div>
  );
}

export default ResourceRateTablesDialog;
